import type { Lang } from "@iyk/vf-app-config"

import * as UI from "@iyk/ui"

import { absurd } from "effect"
import { translations } from "./translations.ts"

export const Footer = (props: Footer.Props) => {
  const t = translations[props.lang]

  return (
    <footer className="relative z-20 flex flex-col gap-24 px-4 pt-24 pb-4">
      <div className="flex flex-col">
        {props.social.map((link, idx) => (
          <a
            key={idx}
            href={link.href}
            className="text-sm text-gray-12 py-2.5 [&:not(:last-child)]:bshadow-b [&:not(:last-child)]:bshadow-gray-4 flex items-center justify-between"
            target="_blank"
            rel="noreferrer"
          >
            <div className="flex items-center gap-2">
              <SocialIcon platform={link.platform} />
              {link.label}
            </div>
            <UI.Icon.ArrowUpRight />
          </a>
        ))}
      </div>
      <div className="flex justify-between items-center">
        <a href="mailto:help@iyk.app" className="text-sm text-gray-10">
          {t.Footer.contactSupport}
        </a>
        <a
          href="https://iyk.app/artists"
          className="text-xs-upper text-gray-10"
          target="_blank"
          rel="noreferrer"
        >
          {t.Footer.poweredBy}
        </a>
      </div>
    </footer>
  )
}

type SocialPlatform = (typeof SocialPlatform)[keyof typeof SocialPlatform]

const SocialPlatform = {
  Instagram: "instagram",
  TikTok: "tiktok",
  Youtube: "youtube",
  Facebook: "facebook",
  Twitter: "twitter",
  Twitch: "twitch",
  Discord: "discord",
  Website: "website",
  Spotify: "spotify",
  Music: "music",
  AppleMusic: "apple-music",
  SoundCloud: "soundcloud"
} as const

const SocialIcon = ({ platform }: { platform: SocialPlatform }): React.ReactNode => {
  switch (platform) {
    case SocialPlatform.Instagram:
      return <UI.Icon.Instagram />
    case SocialPlatform.Facebook:
      return <UI.Icon.Facebook />
    case SocialPlatform.Youtube:
      return <UI.Icon.Youtube />
    case SocialPlatform.TikTok:
      return <UI.Icon.Tiktok />
    case SocialPlatform.Twitter:
      return <UI.Icon.X />
    case SocialPlatform.Discord:
      return <UI.Icon.Discord />
    case SocialPlatform.Website:
      return <UI.Icon.Globe />
    case SocialPlatform.Spotify:
      return <UI.Icon.Spotify />
    case SocialPlatform.Music:
      return <UI.Icon.Music />
    case SocialPlatform.AppleMusic:
      return <UI.Icon.AppleMusic />
    case SocialPlatform.SoundCloud:
      return <UI.Icon.Soundcloud />
    case SocialPlatform.Twitch:
      return null
    default:
      return absurd(platform)
  }
}

namespace Footer {
  export type Props = {
    social: Array<{
      platform: SocialPlatform
      href: string
      label: string
    }>
    lang: Lang
  }
}
